import { UserCollection, UserEntity } from "@lifeat/shared/data-models/user/UserEntity";
import { USERS_NODE } from "@lifeat/shared/rtdb-nodes/RtdbNodes";
import { UserOptInEmail } from "@lifeat/shared/sendgrid/UserOptInEmail";
import * as UserUtil from "@lifeat/shared/user/UserUtil";
import { db, firebase } from "@firebase-local/firebase";
import { logError } from "@service/ConsoleLoggerService";
import { DatabaseService } from "@service/DatabaseService";
import * as LocalStorageService from "@service/LocalStorageService";
import { LocalStorageKeys, fetchFromLocalStorage, saveToLocalStorage } from "@service/LocalStorageService";
import { callMonolith } from "@service/ServerlessFunctionsService";

const SAVED_SPACES_NODE = "savedSpaces";
const LIGHT_MODE_SETTING_NODE = "lightMode";
const ADDITIONAL_DATA_NODE = USERS_NODE.children.ADDITIONAL_USER_SUPPLIED_DATA;

export const updateSavedSpaces = (savedSpaceIds: string[], user: firebase.User): void => {
    if (user && user.emailVerified) {
        const uid = user.uid;
        if (savedSpaceIds.length === 1) {
            db.ref(USERS_NODE.name)
                .child(uid)
                .child(SAVED_SPACES_NODE)
                .set(savedSpaceIds)
                .then(() => {})
                .catch(() => {
                    logError(`Could not update saved spaces [Space IDs: ${savedSpaceIds}] for user [uid: ${uid}].`);
                });
        } else {
            const updates: { [id: string]: any } = {};
            const savedSpacesPath = `/${USERS_NODE.name}/${uid}/${SAVED_SPACES_NODE}`;
            updates[savedSpacesPath] = savedSpaceIds;
            updateUserSettings(uid, updates);
        }
    }
};

const PREFER_DARK_MODE = window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches;
export const fetchLightModeSetting = (userData?: UserEntity): boolean => {
    let lightModeSetting = fetchFromLocalStorage(LocalStorageKeys.LightMode);

    if (lightModeSetting === undefined) {
        lightModeSetting = userData ? userData.lightMode : !PREFER_DARK_MODE;
        LocalStorageService.saveToLocalStorage(LocalStorageService.LocalStorageKeys.LightMode, lightModeSetting);
    }

    return lightModeSetting;
};

export const updateLightModeSetting = (lightModeSetting: boolean, user: firebase.User): void => {
    saveToLocalStorage(LocalStorageKeys.LightMode, lightModeSetting);

    if (user && user.emailVerified) {
        const uid = user.uid;
        const updates: { [id: string]: any } = {};
        const lightModeSettingPath = `/${USERS_NODE.name}/${uid}/${LIGHT_MODE_SETTING_NODE}`;
        updates[lightModeSettingPath] = lightModeSetting;
        updateUserSettings(uid, updates);
    }
};

export const fetchAmbianceVolumeSetting = (userData?: UserEntity) => {
    let ambianceVolumeSetting = 0;
    // if (userData) {
    //     ambianceVolumeSetting = userData.ambianceVolume;
    // } else {
    //     ambianceVolumeSetting = fetchFromLocalStorage(LocalStorageKeys.AmbianceVolume);
    // }
    // ambianceVolumeSetting = fetchFromLocalStorage(LocalStorageKeys.AmbianceVolume);
    return ambianceVolumeSetting;
};

export const updateAmbianceVolumeSetting = (volume: number, user: firebase.User): void => {
    // if (user && user.emailVerified) {
    //     const uid = user.uid;
    //     const updates: {[id: string]: any} = {};
    //     const ambianceVolSettingPath = `/${USERS_NODE.name}/${uid}/${AMBIANCE_VOLUME_SETTING_NODE}`;
    //     updates[ambianceVolSettingPath] = volume;
    //     updateUserSettings(uid, updates);
    // }

    saveToLocalStorage(LocalStorageKeys.AmbianceVolume, volume);
};

/**
 * @deprecated
 * @param uid
 * @param updates
 */
const updateUserSettings = (uid: string, updates: { [id: string]: any }): Promise<any> => {
    return db.ref().update(updates);
};

export const updateFocusModeEnabledSetting = (uid: string, enabled: boolean) => {
    db.ref(`/${USERS_NODE.name}/${uid}/${USERS_NODE.children.FOCUS_MODE_ENABLED}`).set(enabled);
};

export const updateFocusModeIdleTimeout = (uid: string, newTimeoutMs: number) => {
    db.ref(`/${USERS_NODE.name}/${uid}/${USERS_NODE.children.FOCUS_MODE_IDLE_TIMEOUT}`).set(newTimeoutMs);
};

// NOTE: this update will trigger a serverless database function (see SendGridFunctions.ts for more context)
export const updateUserOptInEmailsGeneral = (uid: string, optIn: boolean) => {
    const updates: { [id: string]: boolean } = {};
    updates[UserUtil.getDbPathForEmailType(uid, UserOptInEmail.General)] = optIn;
    return db.ref().update(updates);
};

export const updateUserTypeValue = (uid: string, userType: string) => {
    const userTypePath = `/${USERS_NODE.name}/${uid}/${USERS_NODE.children.USER_TYPE}`;
    db.ref(userTypePath).set(userType);
};

export const updateAcquisitionSourceValue = async (uid: string, acquisitionSource: string) => {
    const userTypePath = `/${USERS_NODE.name}/${uid}/${ADDITIONAL_DATA_NODE.name}/${ADDITIONAL_DATA_NODE.children.ACQUISITION_SOURCE}`;
    await db.ref(userTypePath).set(acquisitionSource);
};

export const updateEmotionalStateValue = async (uid: string, emotionalState: string[]) => {
    const db = DatabaseService();
    await db.set(UserCollection.at(uid).at("additionalUserSuppliedData").at("emotionalState").node, emotionalState);
};

export const updateHoursLostWeeklyValue = async (uid: string, hoursLostWeekly: number) => {
    const db = DatabaseService();
    await db.set(UserCollection.at(uid).at("additionalUserSuppliedData").at("hoursLostWeekly").node, hoursLostWeekly);
};

export const updateHourlyRateValue = async (uid: string, hourlyRate: number) => {
    const db = DatabaseService();
    await db.set(UserCollection.at(uid).at("additionalUserSuppliedData").at("hourlyRate").node, hourlyRate);
};

export const updateRequiresOnboardingVideoValue = async (uid: string, requiresOnboardingVideo: boolean) => {
    const db = DatabaseService();
    await db.set(UserCollection.at(uid).at("requiresOnboardingVideo").node, requiresOnboardingVideo);
};

export const updateOnboardingDataObject = async (additionalDataJsonString: string) => {
    try {
        await callMonolith("update-additional-info-json", { additionalInfoJson: additionalDataJsonString });
    } catch (err) {
        logError(`Error updating onboarding data: ${err}`);
    }
};
